'use client';

import { c as _c } from "react/compiler-runtime";
import { differenceInCalendarDays, formatDistance, formatDistanceStrict } from '@repo/sdk/date';
import { useState, useSyncExternalStore } from 'react';
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const pluralDays = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`;
const untilMidnight = (_time: number, now: number) => new Date(now).setHours(24, 0, 0, 0) - now;

// The days next to today, by name.
const namedDays = new Map([[-1, 'Yesterday'], [0, 'Today'], [1, 'Tomorrow']]);
const overdueBy = (days: string) => `${days} overdue`;

/** Words the days since a passed day, such as "3 days". */
export type PastDays = (days: string) => string;

// What each ticking kind says; only days left read `past`.
export type Ticking = {
  overdue: (time: number, now: number) => boolean;
  text: (time: number, now: number, past?: PastDays) => string;
  wait: (time: number, now: number) => number;
};

/**
 * The kinds whose text changes as time passes. For a date at `time` and the current `now`, each
 * says whether it's overdue, its text, and how long until that text can next change.
 */
export const tickingKinds = {
  // Signed calendar days until the date: "10" ahead, "0" today, "-10" past.
  'day-count': {
    overdue: (time: number, now: number) => differenceInCalendarDays(time, now) < 0,
    text: (time: number, now: number) => String(differenceInCalendarDays(time, now)),
    wait: untilMidnight
  },
  // Whole days either way, rounded, without a suffix: "5 days".
  days: {
    overdue: () => false,
    text: (time: number, now: number) => formatDistanceStrict(time, now, {
      unit: 'day'
    }),
    wait: () => HOUR
  },
  'days-left': {
    overdue: (time: number, now: number) => differenceInCalendarDays(time, now) < 0,
    // "6 days left", or once the day has passed, its days as `past` words them: "3 days overdue".
    text: (time: number, now: number, past: PastDays = overdueBy) => {
      const days = differenceInCalendarDays(time, now);
      return days < 0 ? past(pluralDays(-days)) : `${pluralDays(days)} left`;
    },
    wait: untilMidnight
  },
  relative: {
    overdue: () => false,
    text: (time: number, now: number) => formatDistance(time, now, {
      addSuffix: true
    }),
    wait: (time: number, now: number) => {
      const age = Math.abs(now - time);
      if (age < MINUTE) return SECOND;
      if (age < HOUR) return MINUTE;
      return HOUR;
    }
  },
  // Calendar days from today in words: "Today", "Tomorrow", "In 3 days", "3 days ago".
  'relative-day': {
    overdue: () => false,
    text: (time: number, now: number) => {
      const days = differenceInCalendarDays(time, now);
      const named = namedDays.get(days);
      if (named) return named;
      return days > 0 ? `In ${pluralDays(days)}` : `${pluralDays(-days)} ago`;
    },
    wait: untilMidnight
  }
} satisfies Record<string, Ticking>;
export type TickingKind = keyof typeof tickingKinds;
export const isTicking = (kind: string): kind is TickingKind => Object.hasOwn(tickingKinds, kind);
type Subscriber = {
  kind: TickingKind;
  notify: () => void;
  time: number;
};

// One clock for every mounted ticking date. It runs only while one is mounted,
// waking when the soonest of them can change.
const subscribers = new Set<Subscriber>();
let now = Date.now();
let timer: ReturnType<typeof setTimeout> | undefined;
let isStopped = true;
const schedule = () => {
  clearTimeout(timer);
  timer = undefined;
  if (subscribers.size === 0) return;

  // Measured from the last tick, so dates mounting in between don't postpone it.
  const wait = Math.min(...Array.from(subscribers, ({
    kind,
    time
  }) => tickingKinds[kind].wait(time, now)));
  timer = setTimeout(tick, Math.max(0, now + wait - Date.now()));
};
const tick = () => {
  now = Date.now();
  for (const subscriber of subscribers) subscriber.notify();
  schedule();
};
const subscribe = (subscriber: Subscriber) => {
  // A stopped clock's time is stale, so restart it from the current time.
  if (isStopped) now = Date.now();
  isStopped = false;
  subscribers.add(subscriber);
  schedule();
  return () => {
    subscribers.delete(subscriber);
    schedule();
    // A date re-rendering without the React Compiler resubscribes in the same commit; that
    // isn't a stop, so its time stays put and the date settles instead of re-rendering forever.
    queueMicrotask(() => {
      isStopped = subscribers.size === 0;
    });
  };
};
const getNow = () => now;

/** The current time in ms, kept fresh enough for a `kind` date at `time` to stay current. */
export const useNow = (kind, time) => {
  const $ = _c(3);
  const [renderedAt] = useState(Date.now);
  let t0;
  if ($[0] !== kind || $[1] !== time) {
    t0 = notify => subscribe({
      kind,
      notify,
      time
    });
    $[0] = kind;
    $[1] = time;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const store = useSyncExternalStore(t0, getNow, getNow);
  return Math.max(store, renderedAt);
};